import { type Ref, ref, watch } from 'vue';

export interface UseLocalStorageOptions<T> {
  onError?: (error: Error) => void;
  serializer?: {
    read: (raw: string) => T;
    write: (value: T) => string;
  };
}

/**
 * Reactive LocalStorage composable
 * @param key - Storage key
 * @param initialValue - Initial value if key doesn't exist
 * @param options - Configuration options
 * @returns Reactive ref synced with localStorage
 */
export function useLocalStorage<T>(key: string, initialValue: T, options: UseLocalStorageOptions<T> = {}): Ref<T> {
  const {
    onError = e => {
      if (import.meta.env.DEV) {
        console.error('localStorage error:', e);
      }
    },
    serializer = {
      read: (v: string) => JSON.parse(v) as T,
      write: (v: T) => JSON.stringify(v),
    },
  } = options;

  const data = ref<T>(initialValue) as Ref<T>;

  // SSR safety check
  const isSupported = typeof window !== 'undefined' && window.localStorage;

  // Read from localStorage
  const read = () => {
    if (!isSupported) return initialValue;

    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return initialValue;
      return serializer.read(raw);
    } catch (error) {
      onError(error as Error);
      return initialValue;
    }
  };

  // Write to localStorage
  const write = (value: T) => {
    if (!isSupported) return;

    try {
      localStorage.setItem(key, serializer.write(value));
    } catch (error) {
      onError(error as Error);
    }
  };

  // Initialize
  data.value = read();

  // Watch for changes and sync to localStorage
  watch(
    data,
    newValue => {
      write(newValue);
    },
    { deep: true },
  );

  // Listen for storage events from other tabs
  if (isSupported) {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try {
          data.value = serializer.read(e.newValue);
        } catch (error) {
          onError(error as Error);
        }
      }
    });
  }

  return data;
}
